/**
 * 3.0 콜랩 카드 문구 — 앱 `locales/en/collab.json` 에서 그대로 옮겼다.
 * @description 카드 제목과 숫자 표기는 `createV3.feeCard` · `createV3.fundingCard` · `worksV3.card`,
 *   안내와 배지는 `detailV3.fee` · `detailV3.funding` · `detailV3.checkin` 이다. 키 이름도 앱과 같다.
 */
const plural = (count: number, one: string, other: string) =>
  count === 1 ? one : other;

export const COLLAB_V3_CARD_COPY = {
  ls: (amount: string) => `${amount} LS`,
  detail: "Details",
  verified: "Organizer identity verified",
  fee: {
    title: "Entry fee",
    free: "Free",
    unitSession: "/ session",
    seatsLeft: (count: number) => `${count} spots left`,
    seatFull: "Full",
    caption: {
      confirmed: (amount: string) =>
        `${amount} LS is charged as soon as you join.`,
      conditional: (amount: string) =>
        `Joining holds ${amount} LS; it's charged on the deadline if the minimum is reached.`,
      free: "Taking a seat is free.",
    },
    policy: {
      confirmed: "Cancel before the collab ends for a full refund.",
      conditional:
        "You can cancel anytime until the minimum is reached and the host confirms. If it isn't confirmed, everything is refunded when the collab ends.",
    },
    badge: { committed: "Confirmed", refunded: "Released", cancelled: "Cancelled" },
    sessions: {
      summary: (count: number, next: string, seats: number) =>
        `${count} ${plural(count, "session", "sessions")} • next ${next} • ${seats} seats left`,
      summaryNone: (count: number) =>
        `${count} ${plural(count, "session", "sessions")} • none can be reserved now`,
      closed: "Closed",
    },
    cond: {
      badge: {
        minReached: "Minimum reached",
        full: "Full",
        committed: "Going ahead",
        notReached: "Minimum not reached",
        cancelled: "Cancelled",
      },
      count: (count: number) => `${count} joined`,
      toMin: (count: number) => `${count} more to go`,
      minLabel: (min: number) => `Min ${min}`,
      maxLabel: (max: number) => `Max ${max}`,
      info: {
        collecting: (date: string) =>
          `If the minimum isn't reached by ${date}, the LS is returned automatically.`,
        minReached: (date: string) =>
          `The minimum is reached. It goes ahead automatically on ${date}. If you can't make it, cancel before then.`,
        full: (date: string) =>
          `All spots are taken. It goes ahead automatically on ${date}.`,
        committed: "It's going ahead",
        notReached: "The minimum wasn't reached, so the LS was returned",
        closed: "Closed. The result is coming soon.",
      },
    },
  },
  funding: {
    title: "Fundraising card",
    collected: "raised",
    supporters: (count: number) => `${count} people`,
    goal: (amount: string) => `Goal ${amount} LS`,
    progress: "Fundraising progress",
    purposeLabel: "What it's for",
    badge: {
      SUCCEEDED: "Event confirmed",
      FAILED: "Goal not met",
      CANCELLED: "Cancelled",
    },
    info: {
      open: (date: string) =>
        `If the goal isn't reached by ${date}, your LS will be returned automatically.`,
      succeeded:
        "The goal was reached and the event is confirmed. The held LS has been charged.",
      failed: "The goal wasn't reached, so the LS has been returned.",
      reported: " The host has shared the execution result.",
    },
  },
  works: {
    title: "Works",
    until: (date: string) => `Until ${date}`,
    closed: "Closed",
    formats: "Link • Photo",
    onePerPerson: "1 work each",
    count: (count: number) =>
      `${count} ${plural(count, "work", "works")} posted`,
    empty: "No works yet",
    selectedBy: (name: string) => `Picked by ${name}`,
  },
  checkin: {
    title: "Check-in",
    window: (start: string, end: string) => `${start} – ${end}`,
    count: (count: number) =>
      count === 0 ? "No one has checked in yet" : `${count} checked in`,
  },
} as const;
